import {
  FlagBoxContent,
  useElementSize,
  useWbTreeFlatList,
  WbSearch,
  getOnHoverScrollbarStyle,
  WbTreeLoader,
  WbTreeNode,
} from '@agilelab/plugin-wb-platform';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { FC, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { usePolicyTab } from './PoliciesTabContext';
import { Entity, EntityCard, TypeBadge } from '../../../../Entity';
import { useFlagAndScoreContext } from '../../FlagAndScoreContext';
import { List, RowComponentProps, useDynamicRowHeight } from 'react-window';

const useStyles = makeStyles(theme => ({
  header: {
    padding: `${theme.spacing(1)}px ${theme.spacing(0)}px`,
  },
  selected: { border: 'none', background: theme.palette.bkg.primary },
  tree: {
    overflowY: 'auto',
    height: '100%',
  },
  scroller: {
    ...getOnHoverScrollbarStyle(theme, false),
  },
}));

interface DescendantsTreeProps {
  root: Entity;
  descendants: Map<string, Entity>;
}

// Property _searchLabel for search optimization
type ExtendedEntity = Entity & {
  _searchLabel?: string;
  components: ExtendedEntity[];
};

const getEntityChildren = (entity: ExtendedEntity): ExtendedEntity[] => {
  return entity.components || [];
};

type ReactWindowComponentWrapperProps = RowComponentProps & {
  items: ReactNode[];
};

export const ReactWindowComponentWrapper = ({
  items,
  index,
  style,
  ariaAttributes,
}: ReactWindowComponentWrapperProps) => {
  const item = items[index];
  return (
    <div
      style={{
        ...style,
        paddingRight: 4,
      }}
      {...ariaAttributes}
    >
      {item}
    </div>
  );
};

const Structure: FC<DescendantsTreeProps> = ({ root, descendants }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { clientWidth } = useElementSize(containerRef);

  const rowHeight = useDynamicRowHeight({
    defaultRowHeight: 70,
  });

  const classes = useStyles();
  const { setActiveEntityExternalId, activeEntityExternalId } = usePolicyTab();
  const { policyViolationsCountMap } = useFlagAndScoreContext();

  const [searchValue, setSearchValue] = useState<string>('');

  const resolvedDescendants = useMemo<ExtendedEntity[]>(() => {
    const list = policyViolationsCountMap?.descendants ?? [];

    const resolveEntity = (node: any): ExtendedEntity => {
      const base = descendants.get(node.externalId);

      const resolvedBase: ExtendedEntity = base
        ? ({ ...(base as Entity) } as ExtendedEntity)
        : ({
            id: String(node.id),
            externalId: node.externalId,
            displayName: node.displayName ?? node.externalId,
            kind: node.kind,
            taxonomyId: node.taxonomyId,
            useCaseTemplateId: node.useCaseTemplateId,
            _resolvedType: node._resolvedType ?? 'Unknown',
            components: [],
          } as ExtendedEntity);

      resolvedBase.components = (node.components ?? []).map((child: any) =>
        resolveEntity(child),
      );

      // Precomputing search label
      const name = (resolvedBase.displayName ?? resolvedBase.externalId ?? '')
        .toString()
        .trim();
      resolvedBase._searchLabel = `${name}`.toLowerCase();

      return resolvedBase;
    };

    return list.map(d => resolveEntity(d));
  }, [policyViolationsCountMap?.descendants, descendants]);

  /**
  filteredDescendants: filters the memoized structure when searchValue changes
   * (or when resolvedDescendants changes). Filter function works on precomputed
   * _searchLabel
   */
  const filteredDescendants = useMemo<ExtendedEntity[]>(() => {
    if (!searchValue || searchValue.trim() === '') return resolvedDescendants;

    const q = searchValue.toLowerCase();

    const filterEntity = (entity: ExtendedEntity): ExtendedEntity | null => {
      const matches = !!(
        entity._searchLabel && entity._searchLabel.includes(q)
      );

      const filteredChildren = (entity.components ?? [])
        .map(child => filterEntity(child as ExtendedEntity))
        .filter((c): c is ExtendedEntity => c !== null);

      if (matches || filteredChildren.length > 0) {
        return {
          ...entity,
          components: filteredChildren,
        };
      }

      return null;
    };

    return resolvedDescendants
      .map(e => filterEntity(e))
      .filter((e): e is ExtendedEntity => e !== null);
  }, [searchValue, resolvedDescendants]);

  const onClickHandler = useCallback(
    (externalId: string) => {
      setActiveEntityExternalId(prev =>
        prev === externalId ? undefined : externalId,
      );
    },
    [setActiveEntityExternalId],
  );

  const renderNode = useCallback(
    (entity: ExtendedEntity) => {
      const id = entity.externalId;

      return (
        <EntityCard
          entity={entity}
          isActiveEntity={activeEntityExternalId === id}
          onClickHandler={onClickHandler}
          rightContent={
            <Box display="flex" style={{ gap: '4px' }} alignItems="center">
              <FlagBoxContent
                policyViolationsOutcomeMap={policyViolationsCountMap?.individualViolations.get(
                  id,
                )}
              />
              {clientWidth > 300 && entity._resolvedType && entity.kind && (
                <TypeBadge
                  typeDisplayName={entity._resolvedType}
                  kind={entity.kind}
                />
              )}
            </Box>
          }
        />
      );
    },
    [
      activeEntityExternalId,
      onClickHandler,
      policyViolationsCountMap,
      clientWidth,
    ],
  );

  const filteredRoot = useMemo(() => {
    return {
      ...root,
      components: filteredDescendants,
    };
  }, [root, filteredDescendants]);

  const items = useWbTreeFlatList([filteredRoot], getEntityChildren);

  const elements = useMemo(
    () =>
      items.map(item => (
        <WbTreeNode
          key={item.data.externalId}
          gap={8}
          top={20}
          node={renderNode(item.data)}
          {...item.treeProps}
        />
      )),
    [items, renderNode],
  );

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box mb={1} mr={0.5}>
        <WbSearch onChange={setSearchValue} />
      </Box>

      {/* Virtualized list, it renders only elements currently on screen to improve performance */}
      <List
        className={classes.scroller}
        overscanCount={5}
        rowComponent={ReactWindowComponentWrapper}
        rowCount={elements.length}
        rowHeight={rowHeight}
        rowProps={{
          items: elements,
        }}
      />
    </div>
  );
};

export const DescendantsTree: FC = () => {
  const classes = useStyles();

  const { root, descendants, isLoading } = useFlagAndScoreContext();

  return (
    <Box
      style={{
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box className={classes.header}>
        <Typography style={{ fontWeight: 500 }}>Structure</Typography>
      </Box>

      {isLoading || !root ? (
        <WbTreeLoader />
      ) : (
        <>
          <Structure root={root} descendants={descendants} />
        </>
      )}
    </Box>
  );
};
